import React, { useState, useCallback } from "react";
import { Chess, PieceSymbol } from "chess.js";
import {
  Chessboard,
  ChessboardProvider,
  PieceDropHandlerArgs,
  SquareHandlerArgs,
  PieceHandlerArgs,
} from "react-chessboard";
import { Paper, Box, Alert, Snackbar } from "@mui/material";

import {
  CastlingRights,
  PromotionMove,
  LastMove,
  STARTING_FEN,
} from "./types";
import { boardMapToFen, fenToPieceMap, getCastlingString } from "./fenUtils";
import { AnalysisToolbar } from "./AnalysisToolbar";
import { GameStatusBanner } from "./GameStatusBanner";
import { EditControls } from "./EditControls";
import { PromotionDialog } from "./PromotionDialog";
import { FenBar } from "./FenBar";
import { usePhysicalBoardSync } from "../usePhysicalBoardSync";

export const ChessBoardAnalysis: React.FC = () => {
  // Mode state: Analysis (default) vs Edit
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  // Analysis Chess engine instance
  const [chess] = useState<Chess>(() => new Chess());
  const [fen, setFen] = useState<string>(STARTING_FEN);
  const [boardOrientation, setBoardOrientation] = useState<"white" | "black">("white");

  // Promotion modal state
  const [promotionMove, setPromotionMove] = useState<PromotionMove | null>(null);

  // Edit Mode state
  const [editPieceMap, setEditPieceMap] = useState<Record<string, string>>(() =>
    fenToPieceMap(STARTING_FEN)
  );
  const [editTurn, setEditTurn] = useState<"w" | "b">("w");
  const [editCastling, setEditCastling] = useState<CastlingRights>({
    K: true,
    Q: true,
    k: true,
    q: true,
  });
  const [editError, setEditError] = useState<string | null>(null);

  // Toast feedback state
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Synchronize with physical ESP32 board
  const {
    customSquareStyles,
    sendBoardSetup,
    setLastMove,
    clearSelection,
  } = usePhysicalBoardSync({
    channelId: "analysis-board",
    chess,
    fen,
    mode: "analysis",
    isEditMode,
    onMoveExecuted: (_move, newFen) => {
      setFen(newFen);
    },
    onPromotionNeeded: (promo) => {
      setPromotionMove(promo);
    },
  });


  // Handle square click (disabled for piece moving in Analysis Mode)
  const handleSquareClick = useCallback(
    ({ square }: SquareHandlerArgs) => {
      // In Analysis Mode, moving pieces from web is disabled (physical board only)
      return;
    },
    []
  );

  // Handle piece click (disabled for piece moving in Analysis Mode)
  const handlePieceClick = useCallback(
    ({ square }: PieceHandlerArgs) => {
      // In Analysis Mode, moving pieces from web is disabled (physical board only)
      return;
    },
    []
  );

  // Handle piece drop (enabled only in Edit Mode; disabled in Analysis Mode)
  const handlePieceDrop = useCallback(
    ({ piece, sourceSquare, targetSquare }: PieceDropHandlerArgs): boolean => {
      if (isEditMode) {
        // Edit Mode: free-form drag & drop
        setEditPieceMap((prevMap) => {
          const nextMap = { ...prevMap };

          if (!piece.isSparePiece && sourceSquare) {
            delete nextMap[sourceSquare];
          }

          if (targetSquare) {
            nextMap[targetSquare] = piece.pieceType;
          }

          const newFen = boardMapToFen(nextMap, editTurn, getCastlingString(editCastling));
          setFen(newFen);
          setEditError(null);
          sendBoardSetup(newFen, true, null);
          return nextMap;
        });
        return true;
      }

      // Analysis Mode: Moving pieces from web is disabled (physical board only)
      return false;
    },
    [isEditMode, editTurn, editCastling, sendBoardSetup]
  );

  // Complete promotion move
  const handlePromotionSelect = (pieceSymbol: PieceSymbol) => {
    if (!promotionMove) return;
    const { from, to } = promotionMove;

    try {
      chess.move({ from, to, promotion: pieceSymbol });
      const newFen = chess.fen();
      const newLastMove: LastMove = { from, to, promotion: pieceSymbol };
      setFen(newFen);
      setLastMove(newLastMove);
      sendBoardSetup(newFen, false, newLastMove);
    } catch (err) {
      console.error("Promotion failed:", err);
    }

    setPromotionMove(null);
    clearSelection();
  };

  // Toggle Edit Mode ⇄ Analysis Mode
  const toggleEditMode = () => {
    if (!isEditMode) {
      const currentFen = chess.fen();
      setEditPieceMap(fenToPieceMap(currentFen));
      setEditTurn(chess.turn());
      setEditCastling({
        K: currentFen.split(" ")[2]?.includes("K") ?? true,
        Q: currentFen.split(" ")[2]?.includes("Q") ?? true,
        k: currentFen.split(" ")[2]?.includes("k") ?? true,
        q: currentFen.split(" ")[2]?.includes("q") ?? true,
      });
      setEditError(null);
      clearSelection();
      setIsEditMode(true);
      sendBoardSetup(currentFen, true, null);
    } else {
      const newFen = boardMapToFen(editPieceMap, editTurn, getCastlingString(editCastling));
      try {
        chess.load(newFen);
        const appliedFen = chess.fen();
        setFen(appliedFen);
        setLastMove(null);
        clearSelection();
        setEditError(null);
        setIsEditMode(false);
        setToastMessage("Position applied to Analysis Mode!");
        sendBoardSetup(appliedFen, false, null);
      } catch (err: any) {
        setEditError(`Cannot start Analysis: ${err.message || "Invalid chess position"}`);
      }
    }
  };

  // Clear all pieces from board in Edit Mode
  const handleClearBoard = () => {
    const emptyMap: Record<string, string> = {};
    setEditPieceMap(emptyMap);
    const newFen = boardMapToFen(emptyMap, editTurn, "-");
    setFen(newFen);
    setEditError(null);
    sendBoardSetup(newFen, true, null);
  };

  // Reset board to standard starting position
  const handleResetBoard = () => {
    if (isEditMode) {
      const startMap = fenToPieceMap(STARTING_FEN);
      setEditPieceMap(startMap);
      setEditTurn("w");
      setEditCastling({ K: true, Q: true, k: true, q: true });
      setFen(STARTING_FEN);
      setEditError(null);
      sendBoardSetup(STARTING_FEN, true, null);
    } else {
      chess.reset();
      const resetFen = chess.fen();
      setFen(resetFen);
      setLastMove(null);
      clearSelection();
      sendBoardSetup(resetFen, false, null);
    }
  };

  // Flip board orientation
  const handleFlipBoard = () => {
    setBoardOrientation((prev) => (prev === "white" ? "black" : "white"));
  };

  // Update edit turn and recompute FEN
  const handleEditTurnChange = (newTurn: "w" | "b") => {
    setEditTurn(newTurn);
    const newFen = boardMapToFen(editPieceMap, newTurn, getCastlingString(editCastling));
    setFen(newFen);
    sendBoardSetup(newFen, true, null);
  };

  // Update edit castling rights and recompute FEN
  const handleEditCastlingChange = (key: keyof CastlingRights, checked: boolean) => {
    const nextCastling = { ...editCastling, [key]: checked };
    setEditCastling(nextCastling);
    const newFen = boardMapToFen(editPieceMap, editTurn, getCastlingString(nextCastling));
    setFen(newFen);
    sendBoardSetup(newFen, true, null);
  };

  // Copy FEN to clipboard
  const handleCopyFen = () => {
    navigator.clipboard.writeText(fen);
    setToastMessage("FEN copied to clipboard!");
  };

  // Load custom FEN
  const handleLoadCustomFen = (customFen: string): { success: boolean; error?: string } => {
    try {
      const testChess = new Chess();
      testChess.load(customFen);

      if (isEditMode) {
        setEditPieceMap(fenToPieceMap(customFen));
        setEditTurn(testChess.turn());
        setEditCastling({
          K: customFen.includes("K"),
          Q: customFen.includes("Q"),
          k: customFen.includes("k"),
          q: customFen.includes("q"),
        });
        setFen(customFen);
        sendBoardSetup(customFen, true, null);
      } else {
        chess.load(customFen);
        const appliedFen = chess.fen();
        setFen(appliedFen);
        setLastMove(null);
        clearSelection();
        sendBoardSetup(appliedFen, false, null);
      }

      setToastMessage("Custom FEN loaded successfully!");
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || "Invalid FEN string" };
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        backgroundColor: "#22272e",
        color: "white",
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        maxWidth: 720,
        mx: "auto",
        border: isEditMode ? "2px solid #3b82f6" : "1px solid rgba(255,255,255,0.1)",
        boxShadow: isEditMode ? "0 0 20px rgba(59, 130, 246, 0.25)" : "none",
        transition: "border 0.2s ease, box-shadow 0.2s ease",
      }}
    >
      {/* Header Toolbar */}
      <AnalysisToolbar
        isEditMode={isEditMode}
        onToggleEditMode={toggleEditMode}
        onFlipBoard={handleFlipBoard}
        onResetBoard={handleResetBoard}
        onClearBoard={handleClearBoard}
      />

      {/* Edit Mode Error / Validation Alert */}
      {editError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditError(null)}>
          {editError}
        </Alert>
      )}

      {/* Game Status Banner */}
      <GameStatusBanner chess={chess} fen={fen} isEditMode={isEditMode} />

      {/* Chessboard Provider + Board */}
      <ChessboardProvider
        options={{
          position: fen,
          boardOrientation: boardOrientation,
          allowDragOffBoard: isEditMode,
          canDragPiece: () => isEditMode,
          onPieceDrop: handlePieceDrop,
          onSquareClick: handleSquareClick,
          onPieceClick: handlePieceClick,
          squareStyles: customSquareStyles,
          boardStyle: {
            borderRadius: "8px",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.5)",
          },
          darkSquareStyle: { backgroundColor: "#4b7399" },
          lightSquareStyle: { backgroundColor: "#eaedf1" },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 540, mx: "auto" }}>
          <Chessboard />
        </Box>

        {/* Edit Mode: 2 Rows of Spare Pieces Directly Below the Board */}
        {isEditMode && (
          <EditControls
            editTurn={editTurn}
            editCastling={editCastling}
            onEditTurnChange={handleEditTurnChange}
            onEditCastlingChange={handleEditCastlingChange}
          />
        )}
      </ChessboardProvider>

      {/* FEN Bar at the bottom */}
      <FenBar
        fen={fen}
        onCopyFen={handleCopyFen}
        onLoadCustomFen={handleLoadCustomFen}
      />

      {/* Pawn Promotion Dialog */}
      <PromotionDialog
        promotionMove={promotionMove}
        onPromotionSelect={handlePromotionSelect}
        onClose={() => setPromotionMove(null)}
      />

      {/* Feedback Toast */}
      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={2500}
        onClose={() => setToastMessage(null)}
        message={toastMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Paper>
  );
};

export default ChessBoardAnalysis;
